import React, { useContext, useEffect, useState } from 'react';
import AuthLayout from '../AuthLayout';
import VerifyEmail from './VerifyEmail';
import LoadingSpinner from '../LoadingSpinner';
import api from '../../services/api';
import { AuthContext } from '../../context/AuthContext';

// ---------------------------------------------------------------------------
// Nothing happens on an unproven address.
//
// A seller who has just signed up sees this instead of the app until the code
// comes back. It is a gate rather than a banner because the whole point is
// that the address is not yet known to belong to them — and every useful thing
// the product does eventually sends them mail.
//
// TWO THINGS IT DELIBERATELY DOES NOT DO:
//
//   It does not gate accounts created before verification existed. Those were
//   backfilled as verified in the migration; demanding a code from the entire
//   user base at once would be an outage dressed as a security improvement.
//
//   It does not block on a failed CHECK. If the status call fails — the API is
//   down, the network dropped — the app opens. A verification feature that
//   locks everybody out when it cannot reach the server has done more damage
//   than the problem it solves.
// ---------------------------------------------------------------------------

export default function EmailGate({ children }) {
  const { user } = useContext(AuthContext) || {};
  const [state, setState] = useState({ checking: true, verified: true, email: '', pendingEmail: null });

  useEffect(() => {
    if (!user) { setState({ checking: false, verified: true, email: '', pendingEmail: null }); return undefined; }

    let alive = true;
    api.get('/auth/email/status')
      .then(({ data }) => {
        if (!alive) return;
        setState({
          checking: false,
          verified: Boolean(data.verified),
          email: data.email || '',
          pendingEmail: data.pendingEmail || null
        });
      })
      .catch(() => {
        // Open, deliberately. See the note above.
        if (alive) setState({ checking: false, verified: true, email: '', pendingEmail: null });
      });
    return () => { alive = false; };
  }, [user]);

  if (!user) return children;
  if (state.checking) return <LoadingSpinner />;
  if (state.verified) return children;

  return (
    <AuthLayout
      title="One more step"
      subtitle="Confirm your email to start using your account"
    >
      <VerifyEmail
        email={state.pendingEmail || state.email}
        purpose={state.pendingEmail ? 'change' : 'signup'}
        onVerified={() => setState((s) => ({ ...s, verified: true }))}
      />
    </AuthLayout>
  );
}
